import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { myOrders, type OrderSummary } from '@/lib/checkoutClient';
import { isMockMode, getMockMyOrders } from '@/lib/mock-data';

/**
 * useMyOrders — fetches GET /order/mine. RLS-filtered server-side.
 *
 * No polling — orders update either on user action (refresh button) or
 * window focus refetch is intentionally disabled (we override at
 * queryClient level to avoid stampedes during checkout).
 *
 * staleTime 30s so revisits after Mis Boletas aren't hot-refetching.
 *
 * Mock mode (ROUND6 P2): when `VITE_USE_MOCK_DATA=true`, short-circuits
 * the real fetch and returns deterministic fixtures so demo UX renders
 * even while admin's /order/mine handler is still a 501 stub.
 *
 * `enabled` (ROUND9 UAT-C07): default `true` para los consumers actuales
 * (MyTickets). El Home landing lo pasa `false` para usuarios anónimos —
 * `/order/mine` requiere sesión, así evitamos un 401 inútil en la portada.
 */
export function useMyOrders(enabled = true): UseQueryResult<OrderSummary[]> {
  return useQuery({
    queryKey: ['my-orders', isMockMode() ? 'mock' : 'live'],
    queryFn: async () => {
      if (isMockMode()) {
        // Tiny delay so the loading skeleton is briefly visible — realistic UX.
        await new Promise((r) => setTimeout(r, 150));
        return getMockMyOrders();
      }
      return myOrders();
    },
    enabled,
    staleTime: 30_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
